import {
  getBattlefieldUnitAtZoneIndex,
  isUnitStillOnBattlefield,
} from '../utils/battleLethalDeathFx.js';
import decommissionAnimationDetector from './decommissionAnimationDetector.js';

const { isDecommissionedUnitInHand, DECOMMISSION_DEPLOY_PROVIDER_IDS } = decommissionAnimationDetector;

export const ENCRAZED_BOMBARDIER_FIRE_ARC_VARIANT = 'encrazed_bombardier_fire_arc';

const SKIP_DEPLOY_DAMAGE_VARIANTS = new Set([
  'frontal_harassment_blue_cross',
]);

export const FIRE_ARC_MIRROR_ZONE_BY_DEPLOYER = {
  your_front: 'opponent_front',
  your_flank: 'opponent_flank',
  opponent_front: 'your_front',
  opponent_flank: 'your_flank',
};

/** Viewer-relative battlefield slots scanned for deploy/strategem AOE damage holds. */
export const VIEWER_BATTLEFIELD_SLOTS = [
  { zone: 'your_front', playerId: 'your' },
  { zone: 'your_flank', playerId: 'your' },
  { zone: 'opponent_front', playerId: 'opponent' },
  { zone: 'opponent_flank', playerId: 'opponent' },
];

/**
 * Compare pre/post game state across all battlefield slots (Inferno Drop, Missile Barrage, etc.).
 */
export function collectBattlefieldHealthHoldTargetsFromStateTransition(
  prevState,
  nextState,
  viewerUserId
) {
  if (!nextState?.state) {
    return [];
  }

  const targets = [];
  for (let slotIndex = 0; slotIndex < 5; slotIndex += 1) {
    for (const { zone, playerId } of VIEWER_BATTLEFIELD_SLOTS) {
      const slotMeta = enrichDeploySlotDamageMeta(
        null,
        { zone, index: slotIndex, playerId },
        prevState,
        nextState,
        viewerUserId
      );
      if (slotMeta) {
        targets.push({
          ...slotMeta,
          targetZone: zone,
          targetIndex: slotIndex,
        });
      }
    }
  }
  return targets;
}

function resolveOpponentId(gameState, viewerUserId) {
  const players = gameState?.players || Object.keys(gameState?.state || {});
  return players.find((id) => String(id) !== String(viewerUserId)) ?? null;
}

function resolveGeneralTargetPlayerId(toSlot, gameState, viewerUserId) {
  if (!toSlot || toSlot.zone !== 'general') {
    throw new Error('resolveGeneralTargetPlayerId: toSlot.zone must be general');
  }
  const pid = toSlot.playerId;
  if (pid === 'your') {
    return String(viewerUserId);
  }
  if (pid === 'opponent') {
    const opponentId = resolveOpponentId(gameState, viewerUserId);
    if (!opponentId) {
      throw new Error('resolveGeneralTargetPlayerId: opponent id is required');
    }
    return String(opponentId);
  }
  if (pid == null) {
    throw new Error('resolveGeneralTargetPlayerId: toSlot.playerId is required');
  }
  return String(pid);
}

/**
 * Compare pre/post HP for one viewer-relative battlefield slot.
 * Returns null when no deploy damage FX should run for that slot.
 */
export function enrichDeploySlotDamageMeta(deployUnitId, toSlot, prevState, nextState, viewerUserId) {
  if (!toSlot || toSlot.zone === 'general' || toSlot.index == null) {
    return null;
  }
  if (!nextState?.state) {
    return null;
  }

  const opponentId = resolveOpponentId(nextState, viewerUserId);
  const { zone, index } = toSlot;

  const liveUnit = getBattlefieldUnitAtZoneIndex(nextState, viewerUserId, opponentId, zone, index);
  const prevUnit = prevState
    ? getBattlefieldUnitAtZoneIndex(prevState, viewerUserId, opponentId, zone, index)
    : null;

  if (prevUnit && !liveUnit && typeof prevUnit.health === 'number' && prevUnit.health > 0) {
    const targetPlayerId = toSlot?.playerId
      ?? (toSlot?.zone?.startsWith('your_') ? viewerUserId : opponentId);
    const targetHand = targetPlayerId ? nextState.state?.[targetPlayerId]?.hand : null;
    const deployDecommissionsTarget =
      deployUnitId != null
      && DECOMMISSION_DEPLOY_PROVIDER_IDS.includes(Number(deployUnitId));
    if (deployDecommissionsTarget || isDecommissionedUnitInHand(targetHand, prevUnit)) {
      return null;
    }
    if (targetPlayerId && isUnitStillOnBattlefield(nextState, targetPlayerId, prevUnit.instanceId)) {
      return null;
    }
    return {
      targetZone: zone,
      targetIndex: index,
      targetInstanceId: prevUnit.instanceId,
      healthBefore: prevUnit.health,
      healthAfter: 0,
      damageAmount: prevUnit.health,
      ghostUnit: { ...prevUnit },
    };
  }

  if (!liveUnit || !prevUnit || prevUnit.instanceId !== liveUnit.instanceId) {
    return null;
  }

  const healthBefore = prevUnit.health;
  const healthAfter = liveUnit.health;
  if (typeof healthBefore !== 'number' || typeof healthAfter !== 'number' || healthAfter >= healthBefore) {
    return null;
  }

  return {
    targetZone: zone,
    targetIndex: index,
    targetInstanceId: liveUnit.instanceId,
    healthBefore,
    healthAfter,
    damageAmount: healthBefore - healthAfter,
    ghostUnit: { ...prevUnit },
  };
}

export function enrichEncrazedBombardierFireArcDamageMeta(anim, prevState, nextState, viewerUserId) {
  if (!anim?.fromSlot?.zone) {
    throw new Error('enrichEncrazedBombardierFireArcDamageMeta: fromSlot.zone is required');
  }
  const targetRowZone = FIRE_ARC_MIRROR_ZONE_BY_DEPLOYER[anim.fromSlot.zone];
  if (!targetRowZone) {
    throw new Error(
      `enrichEncrazedBombardierFireArcDamageMeta: unsupported fromSlot.zone "${anim.fromSlot.zone}"`
    );
  }
  if (!nextState?.state) {
    return anim;
  }

  const targetPlayerId = targetRowZone.startsWith('your_') ? 'your' : 'opponent';
  const zoneDamageTargets = [];

  for (let index = 0; index < 5; index += 1) {
    const slotMeta = enrichDeploySlotDamageMeta(
      anim.deployUnitId,
      { zone: targetRowZone, index, playerId: targetPlayerId },
      prevState,
      nextState,
      viewerUserId
    );
    if (slotMeta) {
      zoneDamageTargets.push(slotMeta);
    }
  }

  return {
    ...anim,
    zoneDamageTargets,
  };
}

/**
 * Compare pre/post general HP for deploy projectile damage FX (e.g. First Wave Ascendant vs general).
 */
export function enrichDeployGeneralDamageMeta(anim, prevState, nextState, viewerUserId) {
  if (!anim?.toSlot || anim.toSlot.zone !== 'general') {
    return anim;
  }
  if (anim.deployAnimationVariant && SKIP_DEPLOY_DAMAGE_VARIANTS.has(anim.deployAnimationVariant)) {
    return anim;
  }
  if (!nextState?.state) {
    return anim;
  }

  const playerId = resolveGeneralTargetPlayerId(anim.toSlot, nextState, viewerUserId);
  const prevHealth = prevState?.state?.[playerId]?.health;
  const nextHealth = nextState.state[playerId]?.health;

  if (typeof prevHealth !== 'number' || typeof nextHealth !== 'number') {
    return anim;
  }
  if (nextHealth >= prevHealth) {
    return anim;
  }

  return {
    ...anim,
    targetType: 'general',
    isGeneralTarget: true,
    damageAmount: prevHealth - nextHealth,
    generalHealthBefore: prevHealth,
    generalHealthAfter: nextHealth,
    targetGeneralPlayerId: playerId,
  };
}

/**
 * Compare pre/post deploy game state and attach HP metadata for deploy projectile damage FX.
 */
export function enrichDeployAnimationDamageMeta(anim, prevState, nextState, viewerUserId) {
  if (anim.deployAnimationVariant === ENCRAZED_BOMBARDIER_FIRE_ARC_VARIANT) {
    return enrichEncrazedBombardierFireArcDamageMeta(anim, prevState, nextState, viewerUserId);
  }
  if (!anim?.toSlot) {
    return anim;
  }
  if (anim.toSlot.zone === 'general') {
    return enrichDeployGeneralDamageMeta(anim, prevState, nextState, viewerUserId);
  }
  if (anim.toSlot.index == null) {
    return anim;
  }
  if (anim.deployAnimationVariant && SKIP_DEPLOY_DAMAGE_VARIANTS.has(anim.deployAnimationVariant)) {
    return anim;
  }
  if (!nextState?.state) {
    return anim;
  }

  const slotMeta = enrichDeploySlotDamageMeta(
    anim.deployUnitId,
    anim.toSlot,
    prevState,
    nextState,
    viewerUserId
  );
  if (!slotMeta) {
    return anim;
  }

  return {
    ...anim,
    ...slotMeta,
  };
}

/** Hold pre-hit HP during deploy projectile flight (survivor or lethal). */
export function shouldHoldHealthForDeployTargetAnimation(anim) {
  return resolveDeployHealthHoldTargets(anim).length > 0;
}

/**
 * Normalize single-target and fire-arc deploy payloads into hold/countdown slot metadata.
 */
export function resolveDeployHealthHoldTargets(anim) {
  if (Array.isArray(anim?.zoneDamageTargets) && anim.zoneDamageTargets.length > 0) {
    return anim.zoneDamageTargets.filter(
      (target) => (
        target?.targetInstanceId
        && typeof target.healthBefore === 'number'
        && typeof target.healthAfter === 'number'
        && target.healthBefore > target.healthAfter
        && target.targetZone != null
        && target.targetIndex != null
      )
    );
  }

  if (
    !anim?.targetInstanceId
    || typeof anim.healthBefore !== 'number'
    || typeof anim.healthAfter !== 'number'
    || anim.healthBefore <= anim.healthAfter
  ) {
    return [];
  }

  const targetZone = anim.targetZone ?? anim.toSlot?.zone;
  const targetIndex = anim.targetIndex ?? anim.toSlot?.index;
  if (targetZone == null || targetIndex == null) {
    throw new Error(
      `resolveDeployHealthHoldTargets: missing target slot for ${anim.targetInstanceId}`
    );
  }

  return [{
    targetInstanceId: anim.targetInstanceId,
    healthBefore: anim.healthBefore,
    healthAfter: anim.healthAfter,
    damageAmount: anim.damageAmount,
    ghostUnit: anim.ghostUnit,
    targetZone,
    targetIndex,
  }];
}

/** Sync guard: deploy/strategem pre-hit holds registered before React animation state updates. */
export function trackPendingProjectileHealthHolds(pendingInstanceIds, targets) {
  if (!pendingInstanceIds || !Array.isArray(targets)) {
    return;
  }
  targets.forEach((target) => {
    if (target?.targetInstanceId) {
      pendingInstanceIds.add(target.targetInstanceId);
    }
  });
}

export function releasePendingProjectileHealthHold(pendingInstanceIds, instanceId) {
  if (!pendingInstanceIds || !instanceId) {
    return;
  }
  pendingInstanceIds.delete(instanceId);
}

export function hasPendingProjectileHealthHold(pendingInstanceIds, instanceId) {
  return Boolean(pendingInstanceIds && instanceId && pendingInstanceIds.has(instanceId));
}

/**
 * Turn-boundary cleanup must keep deploy/strategem projectile holds even when animation
 * state has not flushed to React yet (same synchronous WS tick as turn-played).
 */
export function shouldKeepProjectileHealthHoldAtTurnBoundary(
  instanceId,
  pendingInstanceIds,
  hasActiveProjectileAnimation
) {
  if (hasPendingProjectileHealthHold(pendingInstanceIds, instanceId)) {
    return true;
  }
  if (typeof hasActiveProjectileAnimation === 'function' && hasActiveProjectileAnimation(instanceId)) {
    return true;
  }
  return false;
}
